/**
 * Sender statistics Redis operations
 * Individual keys per sender for efficient reads/writes
 */

import { logger } from '../logger';
import { logError } from '../utils';
import type { SenderStat } from '../types';
import { getRedisClient, isRedisAvailable } from './client';
import { extractDomain } from '../scoring/client';
import { getDaysAgo, isEntryExpired } from '../utils/sender-stats';
import type { Redis } from '@upstash/redis';

/**
 * Number of days before sender statistics expire.
 * Statistics older than this are considered expired and removed.
 */
export const SENDER_STATS_EXPIRATION_DAYS = 90;

/**
 * TTL in seconds (90 days)
 */
const SECONDS_PER_DAY = 24 * 60 * 60;
const SENDER_STATS_TTL_SECONDS = SENDER_STATS_EXPIRATION_DAYS * SECONDS_PER_DAY;

/**
 * Generate Redis key for a sender stat
 * @param gmailAccountId - Gmail account ID
 * @param senderEmailOrDomain - Sender email or domain
 * @returns Redis key string
 */
function getSenderStatKey(gmailAccountId: string, senderEmailOrDomain: string): string {
  const normalized = senderEmailOrDomain.toLowerCase();
  return `sender-stats:${gmailAccountId}:${normalized}`;
}

/**
 * Get sender statistics for a Gmail account from Redis.
 * Returns both count and full stat for caching.
 * @param gmailAccountId - Gmail account ID
 * @param senderEmailOrDomain - Sender email address or domain
 * @returns Full sender stat or null if not found/expired
 */
export async function getSenderStatKV(
  gmailAccountId: string,
  senderEmailOrDomain: string
): Promise<number> {
  const fullStat = await getSenderStatFullKV(gmailAccountId, senderEmailOrDomain);
  return fullStat?.count ?? 0;
}

/**
 * Get full sender stat from Redis (for caching).
 * @param gmailAccountId - Gmail account ID
 * @param senderEmailOrDomain - Sender email address or domain
 * @returns Full sender stat or null if not found/expired
 */
export async function getSenderStatFullKV(
  gmailAccountId: string,
  senderEmailOrDomain: string
): Promise<SenderStat | null> {
  if (!isRedisAvailable()) {
    logger.warn('Redis not available, returning null for sender stat', {
      gmailAccountId,
      senderEmailOrDomain,
      reason: 'KV_REST_API_URL or KV_REST_API_TOKEN not configured',
      impact: 'Sender stats will repopulate naturally as emails are processed',
    });
    return null;
  }

  try {
    const redis = getRedisClient();
    const key = getSenderStatKey(gmailAccountId, senderEmailOrDomain);
    const value = await redis.get<string>(key);

    if (typeof value !== 'string') {
      return null;
    }

    const stat = parseSenderStat(value, new Date().toISOString(), key);

    // Check if entry is expired (older than expiration period)
    const cutoffDate = getDaysAgo();
    if (isEntryExpired(stat, cutoffDate)) {
      // Entry is expired, return null
      return null;
    }

    return stat;
  } catch (error: unknown) {
    logError(
      'Error getting sender stat from Redis',
      { gmailAccountId, senderEmailOrDomain },
      error
    );
    // Return null on error - data will repopulate naturally as emails are processed
    return null;
  }
}

/**
 * Batch check if senders have recent messages (within expiration period) from Redis.
 * Returns a Set of sender emails that have recent messages.
 * @param gmailAccountId - Gmail account ID
 * @param senderEmails - Array of sender email addresses to check
 * @returns Set of sender emails that have recent messages within expiration period
 */
export async function getRecentSendersKV(
  gmailAccountId: string,
  senderEmails: string[]
): Promise<Set<string>> {
  if (senderEmails.length === 0) {
    return new Set();
  }

  if (!isRedisAvailable()) {
    logger.warn('Redis not available, returning empty set for recent senders', {
      gmailAccountId,
      reason: 'KV_REST_API_URL or KV_REST_API_TOKEN not configured',
      impact: 'Sender stats will repopulate naturally as emails are processed',
    });
    return new Set();
  }

  try {
    const redis = getRedisClient();
    const keys = senderEmails.map((email) => getSenderStatKey(gmailAccountId, email));

    // Batch fetch all keys at once using MGET
    const values = await redis.mget<string[]>(keys);

    const recentSenders = new Set<string>();
    const cutoffDate = getDaysAgo();

    for (let i = 0; i < senderEmails.length; i++) {
      const value = values[i];
      if (typeof value !== 'string') {
        continue; // Key doesn't exist or invalid type
      }

      const normalizedEmail = senderEmails[i].toLowerCase();
      const key = getSenderStatKey(gmailAccountId, normalizedEmail);
      const stat = parseSenderStat(value, new Date().toISOString(), key);

      if (!isEntryExpired(stat, cutoffDate)) {
        recentSenders.add(normalizedEmail);
      }
    }

    return recentSenders;
  } catch (error: unknown) {
    logError('Error batch getting recent senders from Redis', { gmailAccountId }, error);
    // Return empty set on error - data will repopulate naturally as emails are processed
    return new Set();
  }
}

/**
 * Batch fetch full sender stats (email or domain) from Redis.
 *
 * This is used to avoid N+1 Redis calls when callers need both:
 * - "is sender recent?" checks, AND
 * - the full stat for in-memory caching.
 *
 * @param gmailAccountId - Gmail account ID
 * @param senderEmailsOrDomains - Array of sender emails or domains to fetch
 * @returns Map of normalized sender -> SenderStat (only includes non-expired entries)
 */
export async function getSenderStatsBatchFullKV(
  gmailAccountId: string,
  senderEmailsOrDomains: string[]
): Promise<Map<string, SenderStat>> {
  if (senderEmailsOrDomains.length === 0) {
    return new Map();
  }

  if (!isRedisAvailable()) {
    logger.warn('Redis not available, returning empty map for sender stats batch fetch', {
      gmailAccountId,
      reason: 'KV_REST_API_URL or KV_REST_API_TOKEN not configured',
      impact: 'Sender stats will repopulate naturally as emails are processed',
    });
    return new Map();
  }

  try {
    const redis = getRedisClient();
    const normalizedSenders = senderEmailsOrDomains.map((s) => s.toLowerCase());
    const keys = normalizedSenders.map((sender) => getSenderStatKey(gmailAccountId, sender));

    // Single batch fetch for all requested sender keys
    const values = await redis.mget<string[]>(keys);

    const result = new Map<string, SenderStat>();
    const cutoffDate = getDaysAgo();

    for (let i = 0; i < normalizedSenders.length; i++) {
      const value = values[i];
      if (typeof value !== 'string') {
        continue;
      }

      const sender = normalizedSenders[i];
      const key = keys[i];
      const stat = parseSenderStat(value, new Date().toISOString(), key);

      if (!isEntryExpired(stat, cutoffDate)) {
        result.set(sender, stat);
      }
    }

    return result;
  } catch (error: unknown) {
    logError('Error batch getting sender stats (full) from Redis', { gmailAccountId }, error);
    return new Map();
  }
}

/**
 * Parse sender stat from Redis value with error handling.
 * @param value - Redis value (JSON string or null)
 * @param fallbackLastSeen - Fallback last_seen if value is invalid or missing
 * @param key - Redis key (for logging)
 * @returns Parsed sender stat or default stat
 */
function parseSenderStat(value: string | null, fallbackLastSeen: string, key: string): SenderStat {
  if (!value || typeof value !== 'string') {
    return { count: 0, last_seen: fallbackLastSeen };
  }

  try {
    return JSON.parse(value);
  } catch (parseError) {
    logger.warn('Invalid JSON in sender stat, reinitializing', {
      key,
      value,
      error: parseError instanceof Error ? parseError.message : String(parseError),
    });
    return { count: 0, last_seen: fallbackLastSeen };
  }
}

/**
 * Update a single sender stat in Redis using atomic operations.
 * @param redis - Redis client instance
 * @param key - Redis key
 * @param receivedAt - ISO timestamp when message was received
 */
async function updateSingleSenderStatRedis(
  redis: Redis,
  key: string,
  receivedAt: string
): Promise<void> {
  // Get current value or initialize
  const currentValue = await redis.get<string>(key);
  const currentStat = parseSenderStat(currentValue, receivedAt, key);

  // Increment count and update last_seen
  const updatedStat: SenderStat = {
    count: currentStat.count + 1,
    last_seen: receivedAt,
  };

  // Set with TTL (90 days) - Upstash uses options object
  await redis.set(key, JSON.stringify(updatedStat), { ex: SENDER_STATS_TTL_SECONDS });
}

/**
 * Collect email and domain keys for a sender.
 *
 * @example
 * ```typescript
 * const { emailKey, domainKey } = collectSenderKeys('user@example.com', 'gmail-123');
 * // emailKey: 'sender-stats:gmail-123:user@example.com'
 * // domainKey: 'sender-stats:gmail-123:example.com'
 * ```
 *
 * @param senderEmail - Sender email address
 * @param gmailAccountId - Gmail account ID
 * @returns Object with emailKey and optional domainKey
 */
function collectSenderKeys(
  senderEmail: string,
  gmailAccountId: string
): { emailKey: string; domainKey: string | null } {
  const normalizedEmail = senderEmail.toLowerCase();
  const domain = extractDomain(senderEmail);
  const normalizedDomain = domain ? domain.toLowerCase() : null;

  return {
    emailKey: getSenderStatKey(gmailAccountId, normalizedEmail),
    domainKey:
      normalizedDomain && normalizedDomain !== normalizedEmail
        ? getSenderStatKey(gmailAccountId, normalizedDomain)
        : null,
  };
}

/**
 * Update sender statistics for a Gmail account in Redis.
 * @param gmailAccountId - Gmail account ID
 * @param senderEmail - Sender email address
 * @param receivedAt - ISO timestamp when message was received
 */
export async function updateSenderStatKV(
  gmailAccountId: string,
  senderEmail: string,
  receivedAt: string
): Promise<void> {
  if (!isRedisAvailable()) {
    logger.warn('Redis not available, skipping sender stat update', {
      gmailAccountId,
      senderEmail,
      reason: 'KV_REST_API_URL or KV_REST_API_TOKEN not configured',
      impact: 'Sender stats will repopulate naturally as emails are processed',
    });
    return;
  }

  try {
    // Validate receivedAt is a valid ISO timestamp
    if (!receivedAt || isNaN(new Date(receivedAt).getTime())) {
      logger.warn('Invalid receivedAt timestamp', {
        gmailAccountId,
        senderEmail,
        receivedAt,
      });
      return;
    }

    const redis = getRedisClient();
    const { emailKey, domainKey } = collectSenderKeys(senderEmail, gmailAccountId);

    // Update sender email stat
    await updateSingleSenderStatRedis(redis, emailKey, receivedAt);

    // Update domain stat (if domain exists and is not an email address)
    if (domainKey) {
      await updateSingleSenderStatRedis(redis, domainKey, receivedAt);
    }
  } catch (error: unknown) {
    logError('Error updating sender stat in Redis', { gmailAccountId, senderEmail }, error);
    // Don't throw - this is non-critical, data will repopulate naturally
  }
}

/**
 * Maximum batch size for Redis updates to prevent memory issues and timeouts.
 */
const MAX_BATCH_SIZE = 500;

/**
 * Collect all keys (email + domain) that need to be updated from a batch of updates.
 *
 * Strategy:
 * - Email keys: Each message increments count by 1 (track latest receivedAt per key)
 * - Domain keys: Multiple messages from same domain increment count by message count
 *   (track count and latest receivedAt per domain)
 *
 * @example
 * ```typescript
 * const updates = [
 *   { senderEmail: 'user1@example.com', receivedAt: '2024-01-01T00:00:00Z' },
 *   { senderEmail: 'user2@example.com', receivedAt: '2024-01-02T00:00:00Z' },
 * ];
 * const { emailKeys, domainKeys } = collectUpdateKeys(updates, 'gmail-123');
 * // emailKeys: Map with 2 entries (one per sender)
 * // domainKeys: Map with 1 entry (example.com with count: 2)
 * ```
 *
 * @param updates - Array of updates: [{senderEmail, receivedAt}, ...]
 * @param gmailAccountId - Gmail account ID
 * @returns Object with emailKeys map and domainKeyCounts map
 */
function collectUpdateKeys(
  updates: Array<{ senderEmail: string; receivedAt: string }>,
  gmailAccountId: string
): {
  emailKeys: Map<string, string>; // key -> receivedAt (latest)
  domainKeys: Map<string, { count: number; latestReceivedAt: string }>;
} {
  const emailKeys = new Map<string, string>();
  const domainKeys = new Map<string, { count: number; latestReceivedAt: string }>();

  for (const update of updates) {
    // Validate receivedAt
    if (!update.receivedAt || isNaN(new Date(update.receivedAt).getTime())) {
      logger.warn('Invalid receivedAt timestamp in batch update', {
        gmailAccountId,
        senderEmail: update.senderEmail,
        receivedAt: update.receivedAt,
      });
      continue;
    }

    const { emailKey, domainKey } = collectSenderKeys(update.senderEmail, gmailAccountId);

    // Add email key (each message increments count)
    emailKeys.set(emailKey, update.receivedAt);

    // Track domain key with count (multiple messages from same domain increment count)
    if (domainKey) {
      const existing = domainKeys.get(domainKey);
      if (existing) {
        existing.count += 1;
        if (update.receivedAt > existing.latestReceivedAt) {
          existing.latestReceivedAt = update.receivedAt;
        }
      } else {
        domainKeys.set(domainKey, { count: 1, latestReceivedAt: update.receivedAt });
      }
    }
  }

  return { emailKeys, domainKeys };
}

/**
 * Prepare a stat update by parsing current value and incrementing count.
 * @param currentValue - Current Redis value (JSON string or null)
 * @param receivedAt - ISO timestamp when message was received
 * @param increment - Amount to increment count by (default: 1)
 * @param key - Redis key (for logging)
 * @returns Updated sender stat
 */
function prepareStatUpdate(
  currentValue: string | null,
  receivedAt: string,
  increment: number,
  key: string
): SenderStat {
  const currentStat = parseSenderStat(currentValue, receivedAt, key);
  return {
    count: currentStat.count + increment,
    last_seen: receivedAt,
  };
}

/**
 * Batch update sender statistics for a Gmail account in Redis.
 *
 * FIXED: Correctly handles domain counting when multiple senders from same domain.
 *
 * Strategy:
 * - Email keys: Each message increments count by 1
 * - Domain keys: Increment by number of messages affecting that domain
 *
 * @param gmailAccountId - Gmail account ID
 * @param updates - Array of updates: [{senderEmail, receivedAt}, ...]
 */
export async function updateSenderStatsBatchKV(
  gmailAccountId: string,
  updates: Array<{ senderEmail: string; receivedAt: string }>
): Promise<void> {
  if (updates.length === 0) {
    return;
  }

  // Process in chunks if batch is too large
  if (updates.length > MAX_BATCH_SIZE) {
    for (let i = 0; i < updates.length; i += MAX_BATCH_SIZE) {
      const chunk = updates.slice(i, i + MAX_BATCH_SIZE);
      await updateSenderStatsBatchKV(gmailAccountId, chunk);
    }
    return;
  }

  if (!isRedisAvailable()) {
    logger.warn('Redis not available, skipping batch sender stat update', {
      gmailAccountId,
      updateCount: updates.length,
      reason: 'KV_REST_API_URL or KV_REST_API_TOKEN not configured',
      impact: 'Sender stats will repopulate naturally as emails are processed',
    });
    return;
  }

  try {
    const redis = getRedisClient();

    // Collect all keys that need to be updated
    const { emailKeys, domainKeys } = collectUpdateKeys(updates, gmailAccountId);

    // Collect all keys for batch fetch
    const allKeys = [...Array.from(emailKeys.keys()), ...Array.from(domainKeys.keys())];

    // Batch get all current values
    const currentValues = allKeys.length > 0 ? await redis.mget<string[]>(allKeys) : [];

    // Prepare SET operations (we'll pipeline them to avoid N HTTP requests)
    const setOperations: Array<{ key: string; stat: SenderStat }> = [];

    // Update email keys (each increments by 1)
    let valueIndex = 0;
    for (const [emailKey, receivedAt] of emailKeys) {
      const currentValue = currentValues[valueIndex++];
      const updatedStat = prepareStatUpdate(currentValue, receivedAt, 1, emailKey);
      setOperations.push({ key: emailKey, stat: updatedStat });
    }

    // Update domain keys (increment by count of messages affecting that domain)
    for (const [domainKey, { count: incrementCount, latestReceivedAt }] of domainKeys) {
      const currentValue = currentValues[valueIndex++];
      const updatedStat = prepareStatUpdate(
        currentValue,
        latestReceivedAt,
        incrementCount,
        domainKey
      );
      setOperations.push({ key: domainKey, stat: updatedStat });
    }

    // Execute all updates using Upstash REST pipelining (1 HTTP request per chunk)
    const MAX_PIPELINE_COMMANDS = 500;
    for (let i = 0; i < setOperations.length; i += MAX_PIPELINE_COMMANDS) {
      const chunk = setOperations.slice(i, i + MAX_PIPELINE_COMMANDS);
      const p = redis.pipeline();
      for (const { key, stat } of chunk) {
        p.set(key, JSON.stringify(stat), { ex: SENDER_STATS_TTL_SECONDS });
      }
      const results = await p.exec({ keepErrors: true });
      const errorCount = results.reduce((acc, r) => acc + (r.error ? 1 : 0), 0);
      if (errorCount > 0) {
        logger.debug('Failed to update some sender stats in Redis pipeline', {
          gmailAccountId,
          attempted: chunk.length,
          errorCount,
        });
      }
    }
  } catch (error: unknown) {
    logError('Error batch updating sender stats in Redis', { gmailAccountId }, error);
    // Don't throw - this is non-critical, data will repopulate naturally as emails are processed
  }
}
